<script setup>
import OrderProgress from '@/pages/order/components/OrderProgress.vue'
import { useRequireSelfOrderIdentity } from '@/composables/useRequireSelfOrderIdentity'
import { useSelfOrderStore } from '@/stores/selfOrder'
import { formatRupiah } from '@/utils/formatRupiah'

definePage({
  meta: {
    layout: 'blank',
    public: true,
  },
})

const route = useRoute()
const router = useRouter()
const store = useSelfOrderStore()

const isIdentified = useRequireSelfOrderIdentity()

const paymentChoice = ref('cash')
const isSubmitting = ref(false)
const errorMessage = ref('')

const paymentMethods = [
  { value: 'cash', label: 'Bayar di Kasir', description: 'Tunai atau EDC saat pesanan diambil', icon: 'ri-money-dollar-circle-line' },
  { value: 'qris', label: 'QRIS / E-Wallet', description: 'Scan & bayar langsung dari HP Anda', icon: 'ri-qr-code-line' },
]

const itemTotal = item => (Number(item.unit_price) + (item.modifiers || []).reduce((s, m) => s + Number(m.price || 0), 0)) * item.quantity

// Kalau customer masuk ke halaman ini langsung (refresh) tanpa item -> balik ke menu.
onMounted(() => {
  if (!isIdentified)
    return

  if (!store.cart.length) {
    router.replace({ name: 'order-session-token-menu', params: { sessionToken: route.params.sessionToken } })
    return
  }

  // Addendum 07, Modul W: promo/voucher/poin pelanggan sendiri.
  store.fetchActivePromotions()
  store.fetchLoyaltyInfo()
})

const submitCheckout = async () => {
  isSubmitting.value = true
  errorMessage.value = ''

  try {
    const order = await store.checkout(paymentChoice.value)

    if (paymentChoice.value === 'qris') {
      router.replace({
        name: 'order-session-token-payment',
        params: { sessionToken: route.params.sessionToken },
        query: { orderId: order.order_id },
      })
    }
    else {
      router.replace({ name: 'order-session-token-status', params: { sessionToken: route.params.sessionToken } })
    }
  }
  catch (err) {
    errorMessage.value = err?.data?.message || err?.message || 'Checkout gagal. Silakan coba lagi.'
  }
  finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <div class="checkout-screen pb-8">
    <VAppBar flat color="surface" density="comfortable" class="checkout-appbar">
      <template #prepend>
        <VBtn
          icon
          variant="text"
          :to="{ name: 'order-session-token-menu', params: { sessionToken: route.params.sessionToken } }"
        >
          <VIcon icon="ri-arrow-left-line" />
        </VBtn>
      </template>
      <VAppBarTitle>
        Keranjang Anda
      </VAppBarTitle>
      <template #append>
        <VChip v-if="store.cart.length" size="small" color="primary" variant="tonal">
          {{ store.cartCount }} item
        </VChip>
      </template>
    </VAppBar>

    <div class="px-4">
      <OrderProgress :step="3" />
    </div>

    <VContainer>
      <div v-if="!store.cart.length" class="checkout-empty text-center py-12">
        <VIcon icon="ri-shopping-cart-line" size="56" color="disabled" class="mb-3" />
        <p class="text-body-1 font-weight-medium mb-0">
          Keranjang Anda kosong
        </p>
      </div>

      <VRow v-else>
        <!-- Left: items -->
        <VCol cols="12" md="7">
          <VCard variant="flat" class="checkout-card mb-4">
            <VList lines="two" class="pa-0">
              <template v-for="(item, index) in store.cart" :key="item.key">
                <VListItem class="py-3">
                  <VListItemTitle class="font-weight-medium mb-1">
                    {{ item.product_name }} — {{ item.variant_name }}
                  </VListItemTitle>
                  <VListItemSubtitle v-if="item.modifiers?.length">
                    {{ item.modifiers.map(m => m.name).join(', ') }}
                  </VListItemSubtitle>
                  <VListItemSubtitle v-if="item.notes">
                    Catatan: {{ item.notes }}
                  </VListItemSubtitle>

                  <div class="d-flex align-center justify-space-between mt-2">
                    <div class="qty-control">
                      <button type="button" class="qty-btn" @click="store.updateCartQuantity(item.key, item.quantity - 1)">
                        <VIcon icon="ri-subtract-line" size="14" />
                      </button>
                      <span class="qty-value">{{ item.quantity }}</span>
                      <button type="button" class="qty-btn" @click="store.updateCartQuantity(item.key, item.quantity + 1)">
                        <VIcon icon="ri-add-line" size="14" />
                      </button>
                    </div>
                    <span class="font-weight-bold text-primary">
                      {{ formatRupiah(itemTotal(item)) }}
                    </span>
                  </div>

                  <template #append>
                    <VBtn icon variant="text" size="small" color="error" class="align-self-start" @click="store.removeFromCart(item.key)">
                      <VIcon icon="ri-delete-bin-6-line" size="18" />
                    </VBtn>
                  </template>
                </VListItem>
                <VDivider v-if="index < store.cart.length - 1" />
              </template>
            </VList>
          </VCard>
        </VCol>

        <!-- Right: summary -->
        <VCol cols="12" md="5">
          <VCard variant="flat" class="checkout-card checkout-summary mb-4">
            <VCardText>
              <p class="text-subtitle-2 font-weight-bold mb-3">
                Ringkasan Pesanan
              </p>
              <div class="d-flex justify-space-between text-body-2 mb-2">
                <span class="text-medium-emphasis">Subtotal</span>
                <span>{{ formatRupiah(store.cartSubtotal) }}</span>
              </div>

              <!-- Addendum 07, Modul W: Promo & Voucher & Poin (pelanggan pakai sendiri) -->
              <div
                v-if="store.autoAppliedPromotions.length || store.selectablePromotions.length"
                class="mb-3"
              >
                <p class="text-caption font-weight-medium text-medium-emphasis mb-1">
                  Promo Tersedia
                </p>
                <div class="d-flex flex-wrap" style="gap: 4px;">
                  <VChip
                    v-for="promo in store.autoAppliedPromotions"
                    :key="`auto-${promo.id}`"
                    size="small"
                    color="success"
                    variant="tonal"
                    prepend-icon="ri-checkbox-circle-line"
                  >
                    {{ promo.name }} (otomatis)
                  </VChip>
                  <VChip
                    v-for="promo in store.selectablePromotions"
                    :key="`manual-${promo.id}`"
                    size="small"
                    :color="store.selectedPromotionIds.includes(promo.id) ? 'primary' : undefined"
                    :variant="store.selectedPromotionIds.includes(promo.id) ? 'flat' : 'outlined'"
                    @click="store.togglePromotion(promo.id)"
                  >
                    {{ promo.name }}
                  </VChip>
                </div>
              </div>

              <div class="mb-3">
                <div v-if="!store.voucherPreview" class="d-flex align-center" style="gap: 6px;">
                  <VTextField
                    v-model="store.voucherCode"
                    placeholder="Kode voucher"
                    density="compact"
                    variant="outlined"
                    hide-details
                    @keyup.enter="store.checkVoucher"
                  />
                  <VBtn size="small" variant="tonal" @click="store.checkVoucher">
                    Cek
                  </VBtn>
                </div>
                <VChip
                  v-else
                  color="success"
                  variant="tonal"
                  size="small"
                  closable
                  @click:close="store.clearVoucher"
                >
                  Voucher {{ store.voucherPreview.code }} (-{{ formatRupiah(store.voucherPreview.discount_amount) }})
                </VChip>
                <p v-if="store.voucherError" class="text-caption text-error mb-0 mt-1">
                  {{ store.voucherError }}
                </p>
              </div>

              <div
                v-if="store.loyaltyInfo?.uses_points && store.loyaltyInfo?.balance > 0"
                class="mb-3"
              >
                <p class="text-caption text-medium-emphasis mb-1">
                  Saldo Poin Anda: {{ store.loyaltyInfo.balance }}
                  <span v-if="store.loyaltyInfo.min_redeem_points">
                    (min. tukar {{ store.loyaltyInfo.min_redeem_points }})
                  </span>
                </p>
                <div class="d-flex align-center" style="gap: 6px;">
                  <VTextField
                    v-model.number="store.redeemPoints"
                    type="number"
                    min="0"
                    :max="store.loyaltyInfo.balance"
                    density="compact"
                    variant="outlined"
                    hide-details
                    placeholder="Tukar poin"
                  />
                  <VBtn size="small" variant="tonal" @click="store.redeemPoints = store.loyaltyInfo.balance">
                    Maks
                  </VBtn>
                </div>
              </div>

              <div v-if="store.promoDiscountPreview > 0" class="d-flex justify-space-between text-body-2 mb-1">
                <span class="text-medium-emphasis">Diskon Promo</span>
                <span>-{{ formatRupiah(store.promoDiscountPreview) }}</span>
              </div>
              <div v-if="store.voucherDiscountPreview > 0" class="d-flex justify-space-between text-body-2 mb-1">
                <span class="text-medium-emphasis">Diskon Voucher</span>
                <span>-{{ formatRupiah(store.voucherDiscountPreview) }}</span>
              </div>
              <div v-if="store.pointsDiscountPreview > 0" class="d-flex justify-space-between text-body-2 mb-1">
                <span class="text-medium-emphasis">Potongan Poin</span>
                <span>-{{ formatRupiah(store.pointsDiscountPreview) }}</span>
              </div>

              <VDivider class="my-3" />
              <div class="d-flex justify-space-between align-center mb-1">
                <span class="text-h6 font-weight-bold">Total</span>
                <span class="text-h6 font-weight-bold text-primary">{{ formatRupiah(store.cartTotal) }}</span>
              </div>
              <p class="text-caption text-medium-emphasis mb-0">
                Pajak/biaya layanan (jika ada) akan mengikuti pengaturan tenant saat order diproses.
              </p>
            </VCardText>
          </VCard>

          <VCard variant="flat" class="checkout-card">
            <VCardText>
              <p class="text-subtitle-2 font-weight-bold mb-3">
                Metode Pembayaran
              </p>

              <div class="payment-options mb-4">
                <button
                  v-for="method in paymentMethods"
                  :key="method.value"
                  type="button"
                  class="payment-option"
                  :class="{ 'is-selected': paymentChoice === method.value }"
                  @click="paymentChoice = method.value"
                >
                  <span class="payment-option-icon">
                    <VIcon :icon="method.icon" size="20" />
                  </span>
                  <span class="flex-grow-1 text-start">
                    <span class="d-block font-weight-medium">{{ method.label }}</span>
                    <span class="d-block text-caption text-medium-emphasis">{{ method.description }}</span>
                  </span>
                  <VIcon
                    :icon="paymentChoice === method.value ? 'ri-radio-button-fill' : 'ri-radio-button-line'"
                    :color="paymentChoice === method.value ? 'primary' : undefined"
                    size="18"
                  />
                </button>
              </div>

              <VAlert v-if="errorMessage" type="error" variant="tonal" rounded="lg" class="mb-4">
                {{ errorMessage }}
              </VAlert>

              <VBtn block size="large" rounded="lg" color="primary" :loading="isSubmitting" @click="submitCheckout">
                Buat Pesanan
              </VBtn>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>
    </VContainer>
  </div>
</template>

<style scoped>
.checkout-screen {
  background: rgb(var(--v-theme-background));
  min-block-size: 100vh;
}

.checkout-appbar {
  border-block-end: 1px solid rgba(var(--v-theme-on-surface), 0.06);
}

.checkout-card {
  border-radius: 18px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.06);
  background: rgb(var(--v-theme-surface));
}

.checkout-summary {
  position: sticky;
  inset-block-start: 88px;
}

.qty-control {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 2px;
  border-radius: 999px;
  background: rgba(var(--v-theme-on-surface), 0.06);
}

.qty-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  inline-size: 26px;
  block-size: 26px;
  border-radius: 50%;
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-primary));
  box-shadow: 0 1px 3px rgba(var(--v-theme-on-surface), 0.15);
}

.qty-value {
  min-inline-size: 16px;
  text-align: center;
  font-weight: 600;
  font-size: 13px;
}

.payment-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.payment-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  border-radius: 14px;
  border: 1.5px solid rgba(var(--v-theme-on-surface), 0.1);
  transition: border-color .2s ease, background-color .2s ease;
}

.payment-option.is-selected {
  border-color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.06);
}

.payment-option-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  inline-size: 40px;
  block-size: 40px;
  border-radius: 12px;
  background: rgba(var(--v-theme-primary), 0.1);
  color: rgb(var(--v-theme-primary));
  flex-shrink: 0;
}

@media (max-width: 959px) {
  .checkout-summary {
    position: static;
  }
}
</style>
